import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useT } from '@/i18n';
import { ErrorBox, Field } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { adoptSession } from '@/lib/prefs';
import { logError } from '@/lib/log';
import type { AuthSession } from '@/lib/api/types';
import { AuthLayout } from './AuthLayout';
import { isTenantBrand, useBranding } from '@/lib/branding';

/** Email + password form, shared by the login page and the invite page. */
export function LoginForm({ onDone, email: fixedEmail }: { onDone: (s: AuthSession) => void; email?: string }) {
  const { t } = useT();
  const [email, setEmail] = useState(fixedEmail ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const s = await api.auth.login(email.trim(), password);
      adoptSession(s);
      onDone(s);
    } catch (err) {
      logError('auth', 'login failed', err);
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="stack" style={{ gap: 14 }} noValidate={false}>
      {error ? <ErrorBox error={error} /> : null}
      <Field label={t('email')}>{(p) => <input {...p} className="input" type="email" dir="ltr" autoComplete="email" required value={email} readOnly={!!fixedEmail} onChange={(e) => setEmail(e.target.value)} />}</Field>
      <Field label={t('password')}>{(p) => <input {...p} className="input" type="password" dir="ltr" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
      <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? t('loggingIn') : t('loginBtn')}</button>
    </form>
  );
}

export function LoginPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const branding = useBranding((s) => s.branding);
  // A tenant's own domain is invite-only (WS J): no self-service sign-up link.
  const tenant = isTenantBrand(branding);
  return (
    <AuthLayout title={t('loginTitle')} sub={tenant ? branding.product_name : t('loginSub')}>
      <LoginForm onDone={() => navigate(from, { replace: true })} />
      {tenant && !branding.sign_in.signup ? (
        <p className="muted small" style={{ margin: 0 }}>{t('wlInviteOnly')}</p>
      ) : (
        <p className="muted small" style={{ margin: 0 }}>
          {t('noAccount')} <Link to="/register">{t('registerLink')}</Link>
        </p>
      )}
    </AuthLayout>
  );
}
